/**
 * @module utils/qbLinkage/fieldSnapshot
 * @description 从 QB IColumnInfo 提取联动用字段快照
 */

import type { IFieldSnapshot, IQBSnapshot } from './types';

export function toFieldSnapshot(
  raw: unknown,
  builderType: 'dimension' | 'metric',
): IFieldSnapshot | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const row = raw as Record<string, unknown>;
  const id = String(row.id ?? '');
  if (!id) {
    return null;
  }
  const columnName = String(row.column_name ?? row.column ?? '');
  return {
    id,
    table_id: String(row.table_id ?? ''),
    table_code: String(row.table_code ?? 't0'),
    column_name: columnName,
    column: columnName,
    column_display: String(row.column_display ?? ''),
    column_display_en: String(row.column_display_en ?? ''),
    column_type: row.column_type as string | undefined,
    builder_type: builderType,
    oldColumn: row.oldColumn as string | undefined,
    sql_expression: row.sql_expression
      ? String(row.sql_expression)
      : undefined,
    column_custom: row.column_custom === true,
  };
}

export function snapshotList(
  list: readonly unknown[],
  builderType: 'dimension' | 'metric',
): IFieldSnapshot[] {
  const out: IFieldSnapshot[] = [];
  for (const item of list) {
    const snap = toFieldSnapshot(item, builderType);
    if (snap) {
      out.push(snap);
    }
  }
  return out;
}

export function fieldKey(snap: IFieldSnapshot): string {
  return `${snap.table_code || 't0'}_${snap.column_name}`;
}

/**
 * commit prevSnapshot 时调用：深拷贝维/指为 IFieldSnapshot 值类型
 *
 * model 原地改 column_name 后，若 prev 仍指向同一 IColumnInfo 对象，则
 * isDimensionCategoryChanged / diffRenames 读到的 prev 与 next 都是新名 → 联动不触发。
 * 自定义字段改名主路径在 model.updateCustomColumn + applyCustomColumnRenameOnQB；
 * freeze 保证 QB 联动管道作为兜底时仍能检出 fieldIdentityRenamed。
 */
export function freezeQBSnapshot(snapshot: IQBSnapshot): IQBSnapshot {
  return {
    columnDimensions: snapshotList(snapshot.columnDimensions, 'dimension'),
    columnMetrics: snapshotList(snapshot.columnMetrics, 'metric'),
    columnRawColumns: snapshotList(snapshot.columnRawColumns ?? [], 'metric'),
    columnFilters: Array.isArray(snapshot.columnFilters)
      ? [...snapshot.columnFilters]
      : [],
    columnUserFilters: Array.isArray(snapshot.columnUserFilters)
      ? [...snapshot.columnUserFilters]
      : [],
    hasDateDimension: snapshot.hasDateDimension,
    mode: snapshot.mode,
  };
}

/** 明细模式 columnRawColumns 语义签名（不含 aggregate） */
export function rawColumnListSignature(list: readonly unknown[] | undefined): string {
  return fieldListSignature(list ?? [], 'dimension');
}

/**
 * 指标区专用签名片段：在通用字段签名后追加 aggregate。
 *
 * 仅改聚合方式时 id/column_name 不变，若不纳入签名则 isMetricCategoryChanged 为 false，
 * column_sort 的 sanitize 管道不会运行，维指同名字段的指标列展示名无法刷新。
 */
function metricFieldSignatureSegment(raw: unknown): string | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const row = raw as Record<string, unknown>;
  const id = String(row.id ?? '');
  if (!id) {
    return null;
  }
  const columnName = String(row.column_name ?? row.column ?? '');
  const aggregate = String(row.aggregate ?? '').trim();
  return `${id}|${columnName}|${String(row.column_display ?? '')}|${String(row.column_display_en ?? '')}|${String(row.column_type ?? '')}|${aggregate}`;
}

/**
 * 维/指列表「语义签名」— 按字段 id、column_name、展示名等拼成字符串。
 *
 * 用途：
 * 1. detectCategoryDelta：判断 QB 类别是否变化
 * 2. useQueryColumnLists：DVA 原地 mutate 后 shallowEqual 失效时的相等比较
 *
 * 两处 MUST 共用同一实现，保证「UI 刷新了」与「联动 diff 触发了」一致。
 *
 * 指标区额外包含 aggregate（见 metricFieldSignatureSegment），与 detectMetricChanges
 * 中的 metricAggregateChanged 事件配合，覆盖「仅改聚合」场景。
 */
export function fieldListSignature(
  list: readonly unknown[],
  builderType: 'dimension' | 'metric',
): string {
  if (builderType === 'metric') {
    const parts: string[] = [];
    for (const raw of list || []) {
      const seg = metricFieldSignatureSegment(raw);
      if (seg) {
        parts.push(seg);
      }
    }
    return parts.join('\n');
  }

  return snapshotList(list, builderType)
    .map(
      f =>
        `${f.id}|${f.column_name}|${f.column_display}|${f.column_display_en}|${f.column_type ?? ''}`,
    )
    .join('\n');
}
